import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import type { DragEvent, KeyboardEvent as ReactKeyboardEvent, ReactNode, UIEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { Avatar, Button, InlineError, Skeleton } from '../../components/ui';
import { PageHeader } from '../../layouts/Shells';
import { useAuth } from '../auth/AuthProvider';
import { useTapPeople } from '../projects/hooks';
import { CommentEditor } from '../comments/CommentEditor';
import type { CommentEditorHandle } from '../comments/CommentEditor';
import {
  commentErrorMessage,
  fetchOlderRoomComments,
  fetchCommentsByIds,
} from '../comments/queries';
import { ConsoleQuoteBlock } from '../huddlePortal/ConsoleConversation';
import { jumpToMessage, mergeMessages } from '../huddlePortal/consoleConversationState';
import { fetchVisibleSources } from './drop3c';
import type { ConsoleQuote } from '../huddlePortal/huddleApi';
import { usePasteImageUpload } from '../comments/pasteImage';
import { pasteImageErrorMessage } from '../comments/pasteImage';
import { useCreateComment, usePinnedComments, useTaskComments } from '../comments/hooks';
import { CHAT_PAGE, EDGE_PX, latestGap, mayHaveOlder, nextLimit, oldestGap } from './chatScroll';
import { useCgAccess } from '../cgOrders/hooks';
import { useSalesAccess } from '../tracker/hooks';
import { canSee, useMyPermissions } from '../auth/useMyPermissions';
import { isRichTextEmpty } from '../../components/RichText';
import { ReconnectDriveButton } from '../auth/ReconnectDrive';
import { formatCommentTime } from '../comments/CommentsSection';
import { CommentBody } from '../comments/CommentBody';
import {
  attachmentErrorMessage,
  attachmentSizeError,
  useAttachFileUpload,
} from '../comments/attachFile';
import {
  useAgentIds,
  useApplyFeedback,
  useChatPeople,
  useHuddleChatUnread,
  useMarkRoomRead,
  useMessageFeedback,
  useRefreshRoomThread,
  useRoomLatestMessages,
  useRoomMembers,
  useRoomUnread,
  useRooms,
  useSaveFeedbackReason,
} from './hooks';
import { groupMemberLine, roomTitle, roomUnreadBadgeText } from './rooms';
import type { RoomListItem, RoomMember } from './rooms';
import { roomShowsNewestFirst } from './newestFirst';
import { NewChatDialog } from './NewChatDialog';
import type { NewChatMode } from './NewChatDialog';
import {
  mentionNotDeliveredMessage,
  mentionRefusedMessage,
  roomMentionPeople,
  roomMentionScope,
} from './mentionPeople';
import { personMentionsIn } from '../comments/commentHtml';
import { MakeTaskDialog } from './MakeTaskDialog';
import { LinkPreviewCard } from './LinkPreviewCard';
import { parseHubLink, parseOpenableLink, MAX_PREVIEWS_PER_MESSAGE } from './linkPreview';
import { MessageRecordDialog } from './MessageRecordDialog';
import { escapeHtmlText, inertLinks } from './inertHtml';
import { isReadOnlyUser } from '../projects/types';
import type { CommentWithAuthor } from '../comments/types';
import { COMMENT_BODY_MAX_LENGTH } from '../comments/types';
import { CommentReactions } from '../comments/CommentReactions';
import { useCommentReactions, useToggleReaction } from '../comments/reactionHooks';
import { mayReact, reactionErrorMessage } from '../comments/reactions';
import type { ReactionSummary } from '../comments/reactions';
import { MessageThumbs } from './MessageThumbs';
import { DesktopAlertsButton } from './DesktopAlertsControls';
import { feedbackActionFor, feedbackErrorMessage } from './feedback';
import type { Grade, MessageFeedback } from './feedback';
import { HUDDLE_CHAT_ID } from './huddleChat';
import { HuddleChatView } from './HuddleChatView';
import { HuddleMessage } from '../huddles/HuddleMessage';
import { huddlesByRoomCommentId } from '../huddles/huddleCardApi';
import { useRoomHuddles } from '../huddles/hooks';
import { HuddleBar, HuddleStartButton, RoomHuddleDot } from '../huddles/HuddleBar';
import { useHuddleCall } from '../huddles/HuddleProvider';
import {
  TOMBSTONE_TEXT,
  PIN_LIMIT,
  drop3cErrorMessage,
  forwardHeader,
  forwardTargets,
  isEmptyShell,
  splitThreads,
} from './drop3c';
import type { MessageExtras, ThreadSummary } from './drop3c';
import {
  useDeleteMessage,
  useEditMessage,
  usePostThreadReply,
  useRoomExtras,
  useRoomPins,
  useTogglePin,
  useVisibleSources,
} from './drop3cHooks';
import { MessageActionBar, MessageActionSheet, isTouchScreen } from './MessageActions';
import type { MessageActionSet } from './MessageActions';
import { DeleteMessageDialog, ForwardDialog, PinsStrip, firstLine } from './Drop3cPanels';
import './messages.css';

const NARROW_MESSAGES_WIDTH = 760;

function subscribeToViewport(onChange: () => void) {
  window.addEventListener('resize', onChange);
  return () => window.removeEventListener('resize', onChange);
}

function narrowViewport() {
  return window.innerWidth <= NARROW_MESSAGES_WIDTH;
}

/**
 * The Messages page (mockup/Main.dc.html): room list, thread, composer.
 *
 * Reuses task_comments the same way a project's Discussion thread does —
 * project_id = the room id, task_id = null — through fetchTaskComments and
 * createComment (comments/queries.ts, wired via useTaskComments/
 * useCreateComment, comments/hooks.ts). No new table, no new RPC.
 *
 * Drop 3a: the channel rooms, thread, composer, Make it a task.
 * Drop 3b-1: Group chats and Direct messages in the room list (from
 * create_group_room / create_dm, 20270180000100), the two "+" buttons that
 * start them (hidden for a TAP Viewer, whom the database refuses), a count of
 * unread messages on each row (my_room_unread) and marking a room read when it
 * is opened and when a new message arrives while it is open (mark_room_read).
 * A DM is titled with the other person's name, never its stored name.
 * Drop 3b-2: thumbs up / down on agent messages (MessageThumbs: everyone who
 * can see the room sees the counts, who gave them and the "What was wrong?"
 * notes; admins and members grade), the shared quick reactions row on every
 * message (CommentReactions, the same component task comments use), and the
 * "Alex Huddle" entry (HuddleChatView), shown only when my_huddle_chat_unread
 * says the person has the chat.
 */

/** The sidebar row for one room, DM or group. */
function RoomRow({
  room,
  title,
  members,
  myId,
  active,
  preview,
  unread,
  onOpen,
}: {
  room: RoomListItem;
  title: string;
  members: RoomMember[] | undefined;
  myId: string;
  active: boolean;
  preview: { authorName: string; bodyPreview: string } | undefined;
  unread: number | undefined;
  onOpen: () => void;
}) {
  // The open room is being read right now: no badge on it.
  const badge = active ? null : roomUnreadBadgeText(unread);
  const other = room.kind === 'dm' ? (members ?? []).find((m) => m.userId !== myId) : undefined;
  return (
    <button
      type="button"
      className={`messages-room-item${active ? ' messages-room-item--active' : ''}${badge !== null ? ' messages-room-item--unread' : ''}`}
      aria-current={active ? 'true' : undefined}
      onClick={onOpen}
    >
      {room.kind === 'dm' ? (
        <Avatar name={title} imageUrl={other?.avatarUrl ?? null} size="sm" />
      ) : null}
      <span className="messages-room-text">
        <span className="messages-room-title-line">
          <span className="messages-room-name">{title}</span>
          <RoomHuddleDot roomId={room.id} />
          {badge === null ? null : (
            <span className="messages-room-unread" aria-label={`${badge} unread`}>
              {badge}
            </span>
          )}
        </span>
        {room.kind === 'group' ? (
          <span className="messages-room-preview">{groupMemberLine(members, myId)}</span>
        ) : null}
        {preview ? (
          <span className="messages-room-preview">
            {preview.authorName}: {preview.bodyPreview}
          </span>
        ) : null}
      </span>
    </button>
  );
}

/**
 * The "Alex Huddle" row (3b-2). Not a project: it opens HuddleChatView. Only
 * rendered when my_huddle_chat_unread says has_chat.
 */
function HuddleRow({
  active,
  unread,
  onOpen,
}: {
  active: boolean;
  unread: number | undefined;
  onOpen: () => void;
}) {
  const badge = active ? null : roomUnreadBadgeText(unread);
  return (
    <button
      type="button"
      className={`messages-room-item${active ? ' messages-room-item--active' : ''}${badge !== null ? ' messages-room-item--unread' : ''}`}
      aria-current={active ? 'true' : undefined}
      onClick={onOpen}
    >
      <span className="messages-room-text">
        <span className="messages-room-title-line">
          <span className="messages-room-name">Alex Huddle</span>
          {badge === null ? null : (
            <span className="messages-room-unread" aria-label={`${badge} unread`}>
              {badge}
            </span>
          )}
        </span>
        <span className="messages-room-preview">Alex&apos;s comments from your huddle link</span>
      </span>
    </button>
  );
}

function MessageRow({
  comment,
  extras,
  sourceVisible,
  onViewOriginal,
  thumbs,
  reactions,
  actions,
  thread,
  onOpenThread,
  pinned,
  highlighted,
  onOpenSheet,
  editor,
  onOpenLink,
  quote,
  onQuoteJump,
  quoteReadStatus,
  onRetryQuote,
  huddleCard,
}: {
  huddleCard: ReactNode | null;
  quoteReadStatus: 'loading' | 'error' | 'ready';
  onRetryQuote: () => void;
  quote: ConsoleQuote | null;
  onQuoteJump: () => void;
  comment: CommentWithAuthor;
  /** Drop 3c columns; undefined until the extras read lands (treated as plain). */
  extras: MessageExtras | undefined;
  /** Whether this reader can still open a forward's source. */
  sourceVisible: boolean;
  onViewOriginal: (roomId: string, commentId: string) => void;
  /**
   * Set only on an agent's message (F4: author is_agent). Everyone who can
   * see the room gets it, a TAP Viewer included (Kyu, 26 Sept 11:35);
   * canGrade says whether the chips are buttons. Null means no thumbs at all.
   */
  thumbs: {
    feedback: MessageFeedback | undefined;
    myId: string;
    nameOf: (userId: string) => string;
    canGrade: boolean;
    pending: boolean;
    error: string;
    onGrade: (grade: Grade) => void;
    onReason: (reason: string) => void;
  } | null;
  reactions: {
    list: ReactionSummary[] | undefined;
    canReact: boolean;
    pending: boolean;
    onToggle: (emoji: string, mine: boolean) => void;
  };
  /**
   * Drop 3c: the hover bar and the menu. Null for a Viewer and for a
   * tombstone (design-note "Viewers", card scope 7).
   */
  actions: MessageActionSet | null;
  /** "N replies, last reply <time>" under a room message; undefined when none. */
  thread: ThreadSummary | undefined;
  /** Null inside the thread panel, where the line would point at itself. */
  onOpenThread: (() => void) | null;
  pinned: boolean;
  highlighted: boolean;
  /** Phone only: a tap or long press opens the bottom sheet. */
  onOpenSheet: (() => void) | null;
  /**
   * Set while the reader is editing this row: the inline editor with Save and
   * Cancel, shown in place of the body. The page passes onOpenSheet = null
   * and actions = null at the same time, so a tap inside the editor on a phone
   * never opens the action sheet.
   */
  editor: ReactNode | null;
  /** P1: a record link in the message opens in place; true means it did. */
  onOpenLink: (href: string) => boolean;
}) {
  const { client } = useAuth();
  const deleted = (extras?.deletedAt ?? null) !== null;
  const editing = editor !== null;
  const forward = extras?.forwardMeta ?? null;
  const links = useMemo(
    () => (deleted || editing ? [] : inertLinks(comment.body)),
    [comment.body, deleted, editing],
  );
  const previewLinks = useMemo(() => {
    const origin = window.location.origin;
    return links
      .map((href: string) => ({ href, parsed: parseHubLink(href, origin) }))
      .filter(
        (entry: {
          href: string;
          parsed: ReturnType<typeof parseHubLink>;
        }): entry is {
          href: string;
          parsed: NonNullable<ReturnType<typeof parseHubLink>>;
        } => entry.parsed !== null,
      )
      .slice(0, MAX_PREVIEWS_PER_MESSAGE);
  }, [links]);

  // A tap on the row (not on a button, link or field inside it) opens the
  // phone sheet; a long press arrives as contextmenu on phones.
  const openSheetFrom = (target: EventTarget | null) => {
    if (onOpenSheet === null) {
      return;
    }
    if (target instanceof Element && target.closest('button, a, input, textarea, label') !== null) {
      return;
    }
    onOpenSheet();
  };

  return (
    <li
      id={`message-${comment.id}`}
      tabIndex={-1}
      className={`message-row${highlighted ? ' message-row--highlight' : ''}`}
      onClick={(event) => openSheetFrom(event.target)}
      onContextMenu={(event) => {
        if (onOpenSheet !== null) {
          event.preventDefault();
          openSheetFrom(event.target);
        }
      }}
    >
      <Avatar
        name={comment.author_name === '' ? 'Someone' : comment.author_name}
        imageUrl={comment.author_avatar_url}
        size="sm"
      />
      <div className="message-row-body">
        <div className="message-row-meta">
          <span className="message-row-author">{comment.author_name || 'Someone'}</span>
          <span className="message-row-time">
            {formatCommentTime(comment.created_at)}
            {/* A tombstone keeps its old edited_at (the guard stamps only
                deleted_at), so the label is hidden there. */}
            {!deleted && comment.edited_at !== null ? ' (edited)' : ''}
          </span>
          {pinned ? <span className="message-row-pinned">📌 Pinned</span> : null}
        </div>
        {!editing && !deleted && comment.quoted_comment_id ? (
          quoteReadStatus === 'loading' ? (
            <div className="console-quote">Loading original…</div>
          ) : quoteReadStatus === 'error' ? (
            <div className="console-quote">
              <span>Original could not be loaded.</span>
              <button type="button" onClick={onRetryQuote}>
                Retry original
              </button>
            </div>
          ) : (
            <ConsoleQuoteBlock quote={quote} onJump={onQuoteJump} />
          )
        ) : null}
        {editing ? (
          editor
        ) : deleted ? (
          <p className="message-row-deleted">{TOMBSTONE_TEXT}</p>
        ) : huddleCard !== null ? (
          huddleCard
        ) : forward !== null ? (
          <>
            {/* Plain text: the note is stored as text, never HTML (Dev3). */}
            {forward.note === null ? null : <p className="message-row-note">{forward.note}</p>}
            <div className="message-forward">
              <span className="message-forward-header">
                {forwardHeader(forward, formatCommentTime)}
              </span>
              <CommentBody html={comment.body} onOpenLink={onOpenLink} />
              {sourceVisible &&
              extras?.forwardedFromProjectId != null &&
              extras.forwardedFromCommentId != null ? (
                <button
                  type="button"
                  className="messages-link-button"
                  onClick={() =>
                    onViewOriginal(
                      extras.forwardedFromProjectId as string,
                      extras.forwardedFromCommentId as string,
                    )
                  }
                >
                  View original
                </button>
              ) : (
                <span className="message-forward-unavailable">Original not available to you</span>
              )}
            </div>
          </>
        ) : (
          <CommentBody html={comment.body} onOpenLink={onOpenLink} />
        )}
        {previewLinks.length === 0 ? null : (
          <div className="message-row-previews">
            {previewLinks.map((entry) => (
              <LinkPreviewCard
                key={entry.href}
                client={client}
                link={entry.parsed}
                href={entry.href}
                onOpenLink={onOpenLink}
              />
            ))}
          </div>
        )}
        {deleted || editing || thumbs === null ? null : (
          <MessageThumbs
            feedback={thumbs.feedback}
            myId={thumbs.myId}
            nameOf={thumbs.nameOf}
            canGrade={thumbs.canGrade}
            pending={thumbs.pending}
            error={thumbs.error}
            onGrade={thumbs.onGrade}
            onReason={thumbs.onReason}
          />
        )}
        {deleted || editing ? null : (
          <CommentReactions
            reactions={reactions.list}
            canReact={reactions.canReact}
            pending={reactions.pending}
            onToggle={reactions.onToggle}
          />
        )}
        {thread === undefined || onOpenThread === null ? null : (
          <button type="button" className="message-row-replies" onClick={onOpenThread}>
            <span className="message-row-replies-faces">
              {thread.faces.map((face) => (
                <Avatar key={face.authorId} name={face.name} imageUrl={face.avatarUrl} size="xs" />
              ))}
            </span>
            <b>
              {thread.count} {thread.count === 1 ? 'reply' : 'replies'}
            </b>
            <span>last reply {formatCommentTime(thread.lastAt)}</span>
          </button>
        )}
      </div>
      {actions === null || onOpenSheet !== null ? null : <MessageActionBar actions={actions} />}
    </li>
  );
}

export function MessagesPage() {
  const { client, access } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const narrow = useSyncExternalStore(subscribeToViewport, narrowViewport, () => false);
  const rooms = useRooms();
  // /killua may have just made the DM (create_dm), so the room list is read
  // again before going to it; otherwise a first Killua DM shows "Pick a room".
  const commandNavigate = async (href: string) => {
    await rooms.refetch();
    await navigate(href);
  };
  const roomIds = (rooms.data ?? []).map((room) => room.id);
  const latest = useRoomLatestMessages(roomIds);
  const privateRoomIds = (rooms.data ?? [])
    .filter((room) => room.membersOnly)
    .map((room) => room.id);
  const roomMembers = useRoomMembers(privateRoomIds);
  const unread = useRoomUnread();
  const markRead = useMarkRoomRead();
  const refreshThread = useRefreshRoomThread();
  const myId = access.status === 'approved' ? access.profile.id : '';
  const [newChat, setNewChat] = useState<NewChatMode | null>(null);
  const [createdRoomId, setCreatedRoomId] = useState<string | null>(null);
  const [createdRoomError, setCreatedRoomError] = useState(false);
  const roomsSectionRef = useRef<HTMLElement | null>(null);
  const mobileBackRef = useRef<HTMLButtonElement | null>(null);
  const threadPanelBackRef = useRef<HTMLButtonElement | null>(null);

  const channels = (rooms.data ?? []).filter((room) => room.kind === 'channel');
  const groups = (rooms.data ?? []).filter((room) => room.kind === 'group');
  const dms = (rooms.data ?? []).filter((room) => room.kind === 'dm');
  const titleOf = (room: RoomListItem) => roomTitle(room, roomMembers.data?.get(room.id), myId);

  // The Alex Huddle entry (3b-2) shows only when my_huddle_chat_unread says
  // the person owns a live huddle link (has_chat). Asking for it by URL
  // without that falls back to the first room, like any unknown room id.
  const huddle = useHuddleChatUnread();
  const hasHuddleChat = huddle.data?.hasChat === true;
  const requestedRoom = searchParams.get('room');
  const huddleOpen = requestedRoom === HUDDLE_CHAT_ID && hasHuddleChat;
  const mobileThreadOpen =
    huddleOpen ||
    (requestedRoom !== null &&
      requestedRoom !== HUDDLE_CHAT_ID &&
      ((rooms.data ?? []).some((room) => room.id === requestedRoom) ||
        requestedRoom === createdRoomId));
  const mobilePane = narrow ? (mobileThreadOpen ? 'thread' : 'list') : 'desktop';
  const previousMobilePane = useRef<typeof mobilePane | null>(null);

  useEffect(() => {
    // The room query can still be showing its skeleton when a huddle deep
    // link resolves. Wait until the pane and its Back button actually exist.
    if (rooms.isPending) return;
    const previous = previousMobilePane.current;
    previousMobilePane.current = mobilePane;
    if (mobilePane === 'thread' && previous !== 'thread') {
      mobileBackRef.current?.focus();
    } else if (mobilePane === 'list' && previous === 'thread') {
      roomsSectionRef.current?.focus();
    }
  }, [mobilePane, rooms.isPending]);

  async function refreshCreatedRoom(roomId: string) {
    try {
      const result = await rooms.refetch();
      if (result.data?.some((room) => room.id === roomId)) {
        setCreatedRoomId(null);
        setCreatedRoomError(false);
      } else {
        setCreatedRoomError(true);
      }
    } catch {
      setCreatedRoomError(true);
    }
  }

  const defaultRoomId = channels[0]?.id ?? rooms.data?.[0]?.id ?? '';
  const activeRoomId =
    huddleOpen || (narrow && !mobileThreadOpen)
      ? ''
      : requestedRoom === null || requestedRoom === HUDDLE_CHAT_ID
        ? defaultRoomId
        : requestedRoom;
  const activeRoom = (rooms.data ?? []).find((room) => room.id === activeRoomId) ?? null;
  const activeTitle = activeRoom === null ? '' : titleOf(activeRoom);
  // The Agents room reads newest on top, composer first (newestFirst.ts).
  const newestFirst = roomShowsNewestFirst(activeRoom);

  // A TAP Viewer reads every room but never posts (contract 4.3, the same
  // rule ProjectPage applies through canEditProject/isReadOnlyUser). While
  // access is still loading there is no profile to check, so the composer
  // stays hidden rather than flashing on and off.
  const readOnly = access.status !== 'approved' || isReadOnlyUser(access.profile);
  const myName =
    access.status === 'approved' ? access.profile.display_name.trim() || access.profile.email : '';
  const huddleCall = useHuddleCall();
  const roomHuddles = useRoomHuddles(client, activeRoomId);
  const huddleByCommentId = useMemo(
    () => huddlesByRoomCommentId(roomHuddles.isError ? undefined : roomHuddles.data),
    [roomHuddles.data, roomHuddles.isError],
  );
  const cgAccess = useCgAccess();
  const salesAccess = useSalesAccess();
  const permissions = useMyPermissions();
  const dealAccess = canSee(permissions.data, 'deals');

  const people = useTapPeople(true);
  // /killua uses this exact eligible-DM list rather than assuming every
  // readable TAP profile can be messaged. The command stays absent until K1's
  // Killua profile exists and the current person may start a chat with it.
  const chatPeople = useChatPeople(!readOnly);
  const chatPersonIds = new Set((chatPeople.data ?? []).map((person) => person.id));
  const everyone = (people.data ?? []).map((person) => ({
    id: person.id,
    label: person.display_name.trim() !== '' ? person.display_name : person.email,
    email: person.email,
    mayStartDm: chatPersonIds.has(person.id),
  }));
  // A members-only room (DM, group, private channel) offers only its own
  // members in the @ picker; Team and Hub help offer everyone
  // (mentionPeople.ts says why).
  const mentionPeople = roomMentionPeople(
    activeRoom,
    activeRoom === null ? undefined : roomMembers.data?.get(activeRoom.id),
    everyone,
  );

  // Only the newest CHAT_PAGE messages load; scrolling to the old edge asks
  // for more (chatScroll.ts). The window starts over in every room.
  const [window_, setWindow] = useState({ room: '', size: CHAT_PAGE });
  const limit = window_.room === activeRoomId ? window_.size : CHAT_PAGE;
  const baseComments = useTaskComments(null, activeRoomId, null, null, limit);
  const [quotePages, setQuotePages] = useState<{
    room: string;
    reader: string;
    rows: CommentWithAuthor[];
  }>({
    reader: '',
    room: '',
    rows: [],
  });
  const comments = {
    ...baseComments,
    data: useMemo(
      () =>
        baseComments.data === undefined
          ? undefined
          : [
              ...mergeMessages(
                quotePages.room === activeRoomId && quotePages.reader === myId
                  ? quotePages.rows
                  : [],
                baseComments.data,
              ),
            ].reverse(),
      [baseComments.data, quotePages, activeRoomId, myId],
    ),
  };
  const quoteJumpOp = useRef(0);
  const [quoteJumpStatus, setQuoteJumpStatus] = useState('');
  const quoteTarget = useRef('');
  useEffect(() => {
    quoteJumpOp.current++;
    setQuoteJumpStatus('');
  }, [activeRoomId, myId]);
  useEffect(
    () => () => {
      quoteJumpOp.current++;
    },
    [],
  );

  const createComment = useCreateComment(null, activeRoomId);

  // Drop 3b-2: thumbs (agents' messages only) and quick reactions (every
  // message). One read each for the whole open thread. Everyone who can see
  // the room reads the grades on its agent messages, a TAP Viewer too (Kyu,
  // 26 Sept 11:35); only admins and members who did not write it may grade.
  const threadIds = useMemo(() => (comments.data ?? []).map((c) => c.id), [comments.data]);
  const agentIds = useAgentIds();
  const isAgentMessage = (comment: CommentWithAuthor) =>
    agentIds.data?.has(comment.author_id) === true;
  const gradable = (comment: CommentWithAuthor) =>
    !readOnly && comment.author_id !== myId && isAgentMessage(comment);
  const agentMessageIds = useMemo(
    () =>
      (comments.data ?? [])
        .filter((c) => agentIds.data?.has(c.author_id) === true)
        .map((c) => c.id),
    [comments.data, agentIds.data],
  );
  const feedback = useMessageFeedback(agentMessageIds);
  const applyFeedback = useApplyFeedback();
  const saveReason = useSaveFeedbackReason();
  const [feedbackError, setFeedbackError] = useState<{ commentId: string; message: string } | null>(
    null,
  );
  const reactionList = useCommentReactions(threadIds);
  const toggleReaction = useToggleReaction();
  const canReact = mayReact(access.status === 'approved' ? access.profile : null);

  // Who gave a thumb or wrote a note: the same display_name then email the
  // @ picker uses (everyone, above), then a DM or group's own member list,
  // then "Someone" like an unnamed author.
  const activeMembers = activeRoom === null ? undefined : roomMembers.data?.get(activeRoom.id);
  const nameOf = (userId: string) =>
    everyone.find((person) => person.id === userId)?.label ??
    activeMembers?.find((member) => member.userId === userId)?.name ??
    'Someone';

  function thumbsFor(comment: CommentWithAuthor) {
    if (!isAgentMessage(comment)) {
      return null;
    }
    const summary = feedback.data?.get(comment.id);
    const mine = summary?.mine;
    const onFail = (cause: unknown) =>
      setFeedbackError({
        commentId: comment.id,
        message: feedbackErrorMessage(cause instanceof Error ? cause.message : ''),
      });
    return {
      feedback: summary,
      myId,
      nameOf,
      canGrade: gradable(comment),
      pending: applyFeedback.isPending || saveReason.isPending,
      error: feedbackError?.commentId === comment.id ? feedbackError.message : '',
      onGrade: (grade: Grade) => {
        setFeedbackError(null);
        applyFeedback.mutate(
          { commentId: comment.id, action: feedbackActionFor(mine, grade) },
          { onError: onFail },
        );
      },
      onReason: (reason: string) => {
        setFeedbackError(null);
        saveReason.mutate({ commentId: comment.id, reason }, { onError: onFail });
      },
    };
  }

  function reactionsFor(comment: CommentWithAuthor) {
    return {
      list: reactionList.data?.get(comment.id),
      canReact,
      pending: toggleReaction.isPending,
      onToggle: (emoji: string, mine: boolean) => {
        setError('');
        toggleReaction.mutate(
          { commentId: comment.id, emoji, mine },
          {
            onError: (cause) => {
              const message = reactionErrorMessage(cause instanceof Error ? cause.message : '');
              if (message !== '') {
                setError(message);
              }
            },
          },
        );
      },
    };
  }

  // Mark read (mark_room_read, 20270180000100): when a room is opened, and
  // again whenever a newer message shows up in it while it stays open. The
  // key is room + newest message id, so one arrival marks once, not on every
  // render.
  const newestId =
    (comments.data ?? []).reduce<{ id: string; at: string } | null>(
      (best, comment) =>
        best === null || comment.created_at > best.at
          ? { id: comment.id, at: comment.created_at }
          : best,
      null,
    )?.id ?? '';
  const markedKey = useRef('');
  const { mutate: markRoomRead } = markRead;
  useEffect(() => {
    if (activeRoom === null || comments.isPending) {
      return;
    }
    const key = `${activeRoom.id}:${newestId}`;
    if (markedKey.current === key) {
      return;
    }
    markedKey.current = key;
    markRoomRead(activeRoom.id);
  }, [activeRoom, comments.isPending, newestId, markRoomRead]);

  // A new message while the room is open: the unread poll (60s) is what
  // notices it. The open room then reloads its thread, which moves newestId
  // and marks it read again through the effect above.
  const activeUnread = activeRoom === null ? 0 : (unread.data?.get(activeRoom.id) ?? 0);
  useEffect(() => {
    if (activeRoomId === '' || activeUnread <= 0) {
      return;
    }
    refreshThread(activeRoomId);
  }, [activeRoomId, activeUnread, refreshThread]);
  const pasteImage = usePasteImageUpload();
  const attachFile = useAttachFileUpload();

  // Messages has no size check on a pasted screenshot today (only the
  // paperclip and drag-drop paths call attachmentSizeError); the guide
  // promises 25 MB everywhere, so pasting must refuse the same way and reuse
  // the same wording, before the upload starts.
  const pasteImageWithCap = useCallback(
    async (file: File) => {
      const sizeError = attachmentSizeError(file);
      if (sizeError !== null) {
        throw new Error(sizeError);
      }
      return pasteImage(file);
    },
    [pasteImage],
  );

  const [body, setBody] = useState('');
  const [error, setError] = useState('');
  const [needsReconnect, setNeedsReconnect] = useState(false);
  const [taskDialogComment, setTaskDialogComment] = useState<CommentWithAuthor | null>(null);
  // P1 (kyu-20260930-1755-polish): a task link clicked in a message opens
  // over the page instead of navigating, so the thread and scroll stay put.
  const [openRecord, setOpenRecord] = useState<{ taskId: string; href: string } | null>(null);
  const openLinkInPlace = useCallback((href: string) => {
    const parsed = parseOpenableLink(href, window.location.origin);
    if (parsed === null || parsed.kind !== 'task') {
      return false;
    }
    setOpenRecord({ taskId: parsed.id, href });
    return true;
  }, []);
  const closeRecord = useCallback(() => setOpenRecord(null), []);
  const followRecordLink = useCallback(() => {
    // Not openable here (no row, refused, a room, or a deal task under
    // option B): exactly what the link did before.
    if (openRecord !== null) {
      window.location.assign(openRecord.href);
    }
    setOpenRecord(null);
  }, [openRecord]);
  // Edit your own message: one editor at a time. `where` matters because a
  // thread parent is drawn twice at once, in the room list and at the top of
  // the thread panel; only the copy the Edit was picked from opens.
  const [editing, setEditing] = useState<{
    roomId: string;
    commentId: string;
    where: 'room' | 'thread';
  } | null>(null);
  const [editDraft, setEditDraft] = useState('');
  const [editError, setEditError] = useState('');
  // A pasted screenshot still uploading: Send (composer) and Save (edit box)
  // wait for it, or the message goes out without it (design note S16).
  const [uploading, setUploading] = useState(false);
  const [editUploading, setEditUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const editorRef = useRef<CommentEditorHandle>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  function report(cause: unknown) {
    setError(commentErrorMessage(cause instanceof Error ? cause.message : ''));
  }

  function submit() {
    if (uploading) {
      return;
    }
    if (isRichTextEmpty(body) && !body.includes('<img')) {
      setError('Write a message first.');
      return;
    }
    // Who the message names right now, read from its own markup, exactly as
    // a project's Discussion tab does (CommentsSection personMentionsIn).
    // These ids become comment_mentions rows: the Inbox notice (Messages tab,
    // never Slack since npc r3) and what an agent's
    // my_mentions_since reads.
    const mentioned = personMentionsIn(body);
    // The database writes all of one message's mentions in one insert and
    // refuses the lot if one person may not be named here (not an active TAP
    // person, or in a DM or group not a member). Check first against the
    // same list the @ picker offers, so the message is not sent at all and
    // the draft stays put for the person to fix.
    const allowed = new Set(mentionPeople.map((person) => person.id));
    const refused = mentioned.filter((person) => !allowed.has(person.id));
    if (refused.length > 0) {
      setError(mentionRefusedMessage(roomMentionScope(activeRoom), refused));
      return;
    }
    setError('');
    createComment.mutate(
      { body, mentionedUserIds: mentioned.map((person) => person.id) },
      {
        onSuccess: (result) => {
          setBody('');
          // Reload the room with its extras, or the new message has no Edit
          // until a page reload (useRefreshRoomThread).
          refreshThread(activeRoomId);
          // Refused anyway (someone left the chat or was deactivated since
          // the page loaded). The message itself is posted, so the draft is
          // cleared to avoid a second copy, and the person is told plainly
          // that nobody was notified.
          if (result.mentionError !== null) {
            setError(mentionNotDeliveredMessage(result.mentionError));
          }
        },
        onError: report,
      },
    );
  }

  async function handleAttach(file: File) {
    setError('');
    setNeedsReconnect(false);
    const sizeError = attachmentSizeError(file);
    if (sizeError !== null) {
      setError(sizeError);
      return;
    }
    try {
      const uploaded = await attachFile(file);
      editorRef.current?.insertLink(uploaded.name, uploaded.url);
    } catch (cause) {
      const message = attachmentErrorMessage(cause);
      setNeedsReconnect(message.toLowerCase().includes('reconnect'));
      setError(message);
    }
  }

  function onDrop(event: DragEvent<HTMLDivElement>) {
    event.preventDefault();
    setDragActive(false);
    if (readOnly) {
      return;
    }
    const file = event.dataTransfer.files[0];
    if (file !== undefined) {
      void handleAttach(file);
    }
  }

  /* ─── Drop 3c: threads, pins, forward, delete ─── */

  const extras = useRoomExtras(activeRoomId);
  const pins = useRoomPins(activeRoomId);
  const togglePin = useTogglePin(activeRoomId);
  const deleteMsg = useDeleteMessage(activeRoomId);
  const editMsg = useEditMessage(activeRoomId);
  const postReply = usePostThreadReply(activeRoomId);

  const split = useMemo(
    () => splitThreads(comments.data ?? [], extras.data, (c) => c.author_avatar_url),
    [comments.data, extras.data],
  );
  // A tombstone whose last reply went is an empty shell (Dev4 A3): hidden
  // even in the moment before the database removes it.
  const roomMessages = split.topLevel.filter(
    (comment) => !isEmptyShell(comment.id, extras.data, split.replies),
  );
  const sourceIds = useMemo(
    () => [
      ...new Set(
        [
          ...[...(extras.data?.values() ?? [])].map((entry) => entry.forwardedFromCommentId),
          ...(comments.data ?? []).map((row) => row.quoted_comment_id),
        ].filter((id): id is string => Boolean(id)),
      ),
    ],
    [extras.data, comments.data],
  );
  const visibleSources = useVisibleSources(sourceIds);

  const pinnedIds = new Set((pins.data ?? []).map((pin) => pin.commentId));
  const pinsFull = (pins.data?.length ?? 0) >= PIN_LIMIT;
  const byId = new Map((comments.data ?? []).map((comment) => [comment.id, comment]));
  // A pin on a message older than the loaded window is read by id, so the
  // strip keeps showing it (and its Jump widens the window to it).
  const oldPinIds = (pins.data ?? []).map((pin) => pin.commentId).filter((id) => !byId.has(id));
  const oldPins = usePinnedComments(activeRoomId, oldPinIds);
  for (const comment of oldPins.data ?? []) {
    if (!byId.has(comment.id)) {
      byId.set(comment.id, comment);
    }
  }
  const pinnedItems = (pins.data ?? []).flatMap((pin) => {
    const comment = byId.get(pin.commentId);
    return comment === undefined
      ? []
      : [
          {
            commentId: pin.commentId,
            authorName: comment.author_name,
            authorAvatarUrl: comment.author_avatar_url,
            text: firstLine(comment.body),
            createdAt: comment.created_at,
            editedAt: comment.edited_at,
            pinnedByName: pin.pinnedBy === myId ? 'you' : nameOf(pin.pinnedBy),
          },
        ];
  });

  // npc r3: /messages?room=<id>&comment=<message> is where an Inbox notice
  // from Messages lands (notificationHref). The message is marked and scrolled
  // to; when it is a thread reply, its thread opens with it.
  const commentParam = searchParams.get('comment');
  const linkedComment =
    commentParam === null
      ? undefined
      : (comments.data ?? []).find((comment) => comment.id === commentParam);
  const linkedParent =
    linkedComment === undefined
      ? null
      : (linkedComment.parent_comment_id ??
        extras.data?.get(linkedComment.id)?.parentCommentId ??
        null);
  // A6: /messages?room=<id>&thread=<parent> opens that thread on arrival.
  const threadParam = searchParams.get('thread') ?? linkedParent;
  const threadParent =
    threadParam === null ? null : (roomMessages.find((c) => c.id === threadParam) ?? null);
  const threadPanelOpen = narrow && mobilePane === 'thread' && threadParent !== null;
  const previousThreadPanelOpen = useRef(false);

  useEffect(() => {
    const wasOpen = previousThreadPanelOpen.current;
    previousThreadPanelOpen.current = threadPanelOpen;
    if (threadPanelOpen && !wasOpen) {
      threadPanelBackRef.current?.focus();
    } else if (!threadPanelOpen && wasOpen && mobilePane === 'thread') {
      mobileBackRef.current?.focus();
    }
  }, [threadPanelOpen, mobilePane]);
  const threadReplies = threadParent === null ? [] : (split.replies.get(threadParent.id) ?? []);
  const threadParentDeleted =
    (extras.data?.get(threadParent?.id ?? '')?.deletedAt ?? null) !== null;

  function openThread(parentId: string) {
    setSearchParams({ room: activeRoomId, thread: parentId });
  }
  function closeThread() {
    setSearchParams({ room: activeRoomId });
  }

  const touch = useMemo(() => isTouchScreen(), []);
  const [sheetFor, setSheetFor] = useState<{
    comment: CommentWithAuthor;
    isReply: boolean;
    where: 'room' | 'thread';
  } | null>(null);
  const [forwardFor, setForwardFor] = useState<CommentWithAuthor | null>(null);
  const [deleteFor, setDeleteFor] = useState<{
    comment: CommentWithAuthor;
    isReply: boolean;
  } | null>(null);
  const [deleteError, setDeleteError] = useState('');
  const [notice, setNotice] = useState('');
  const [highlightState, setHighlightId] = useState('');
  // A Jump or View original click wins; otherwise the message an Inbox link
  // named (commentParam) is the one marked.
  const highlightId =
    highlightState !== '' ? highlightState : linkedComment === undefined ? '' : linkedComment.id;
  const [threadBody, setThreadBody] = useState('');
  const [threadError, setThreadError] = useState('');

  // Jump, View original: scroll the message into view once it is rendered.
  useEffect(() => {
    if (highlightId === '') {
      return;
    }
    const target = document.getElementById(`message-${highlightId}`);
    target?.scrollIntoView?.({ block: 'center' });
    target?.focus({ preventScroll: true });
  }, [highlightId, comments.data]);

  // A link (Inbox notice, pin Jump, thread link) can name a message older than
  // the window. Widen the window until it is loaded, or the cap is reached.
  useEffect(() => {
    const loaded = comments.data;
    if (loaded === undefined || comments.isPlaceholderData || !mayHaveOlder(loaded.length, limit)) {
      return;
    }
    const wanted = [highlightState, commentParam ?? '', threadParam ?? ''];
    if (wanted.some((id) => id !== '' && !loaded.some((comment) => comment.id === id))) {
      setWindow({ room: activeRoomId, size: nextLimit(limit, true) });
    }
  }, [
    comments.data,
    comments.isPlaceholderData,
    limit,
    highlightState,
    commentParam,
    threadParam,
    activeRoomId,
  ]);

  // Scrolling: load older messages near the old edge, and show "Jump to
  // latest" while the reader is away from the newest one.
  const [awayRoom, setAwayRoom] = useState('');
  function onThreadScroll(event: UIEvent<HTMLDivElement>) {
    const box = event.currentTarget;
    setAwayRoom(latestGap(box, newestFirst) > EDGE_PX ? activeRoomId : '');
    if (
      comments.data !== undefined &&
      !comments.isPlaceholderData &&
      mayHaveOlder(comments.data.length, limit) &&
      oldestGap(box, newestFirst) <= EDGE_PX
    ) {
      setWindow({ room: activeRoomId, size: nextLimit(limit) });
    }
  }
  // 0 is the newest end in both layouts (chatScroll.ts).
  function jumpToLatest() {
    if (threadBodyRef.current !== null) {
      threadBodyRef.current.scrollTop = 0;
    }
    setAwayRoom('');
  }

  // The Agents room opens at the top, where its newest message is. Once per
  // room opened, after its messages have loaded; a Jump or Inbox link to a
  // message wins (the effect above centres that message instead). Every
  // other room keeps the browser's own scroll.
  const threadBodyRef = useRef<HTMLDivElement>(null);
  const landedRoom = useRef('');
  useEffect(() => {
    if (!newestFirst) {
      landedRoom.current = '';
      return;
    }
    if (comments.isPending || landedRoom.current === activeRoomId) {
      return;
    }
    landedRoom.current = activeRoomId;
    if (highlightId !== '') {
      return;
    }
    if (threadBodyRef.current !== null) {
      threadBodyRef.current.scrollTop = 0;
    }
    // The page has no fixed height, so it is the window that scrolls, not
    // the message area (Dev4, t_1df86a1f). Coming from a page scrolled down,
    // the window keeps that offset; bring it back to the top too.
    window.scrollTo?.({ top: 0 });
  }, [newestFirst, activeRoomId, comments.isPending, highlightId]);

  async function jumpQuote(id: string) {
    const op = ++quoteJumpOp.current;
    quoteTarget.current = id;
    setQuoteJumpStatus('Finding message…');
    try {
      const available = (await fetchVisibleSources(client, [id])).get(id);
      if (op !== quoteJumpOp.current) return;
      if (available?.projectId !== activeRoomId) {
        setQuoteJumpStatus('Original unavailable');
        return;
      }
      const source = (await fetchCommentsByIds(client, [id])).find(
        (row) => row.project_id === activeRoomId,
      );
      if (op !== quoteJumpOp.current) return;
      if (!source) {
        setQuoteJumpStatus('Original unavailable');
        return;
      }
      const initial = mergeMessages([], comments.data ?? []);
      const found = await jumpToMessage(
        id,
        initial,
        true,
        async (cursor) => {
          const rows = await fetchOlderRoomComments(client, activeRoomId, cursor);
          return { messages: rows, has_more: rows.length === 25 };
        },
        () => op !== quoteJumpOp.current,
        (rows) => setQuotePages({ room: activeRoomId, reader: myId, rows }),
      );
      if (op !== quoteJumpOp.current) return;
      if (found.status === 'found') {
        const fresh = (await fetchVisibleSources(client, [id])).get(id);
        if (op !== quoteJumpOp.current) return;
        if (fresh?.projectId !== activeRoomId) {
          setQuoteJumpStatus('Original unavailable');
          return;
        }
        setQuotePages({
          room: activeRoomId,
          reader: myId,
          rows: mergeMessages(found.messages, [source]),
        });
        setSearchParams({ room: activeRoomId, comment: id });
        setHighlightId(id);
        setQuoteJumpStatus('');
      } else
        setQuoteJumpStatus(
          found.status === 'ceiling'
            ? 'Keep looking'
            : found.status === 'unavailable'
              ? 'Original unavailable'
              : 'Finding stopped. Retry.',
        );
    } catch {
      if (op === quoteJumpOp.current) setQuoteJumpStatus('Could not find the original. Retry.');
    }
  }

  function actionsFor(
    comment: CommentWithAuthor,
    isReply: boolean,
    where: 'room' | 'thread',
  ): MessageActionSet | null {
    // Viewers get no actions; a tombstone has none (card scope 7 and 10).
    if (readOnly || (extras.data?.get(comment.id)?.deletedAt ?? null) !== null) {
      return null;
    }
    const pinned = pinnedIds.has(comment.id);
    const own = comment.author_id === myId;
    // A forwarded copy carries someone else's words under "Forwarded from":
    // never editable from the page, not even by the person who forwarded it
    // (Shiroe, 28 Sept). The database would let the forwarder change the body;
    // the page is the rule (findings-dev3-edit-message.md, decision A).
    // Until the extras read lands a forward cannot be told from a plain
    // message, so Edit waits for it (Dev4 N2, same as the tombstone check).
    const rowExtras = extras.data?.get(comment.id);
    const isForward = rowExtras === undefined || rowExtras.forwardedFromCommentId != null;
    return {
      onReply: isReply ? null : () => openThread(comment.id),
      onForward: () => {
        setNotice('');
        setForwardFor(comment);
      },
      onMakeTask: () => setTaskDialogComment(comment),
      pin: isReply
        ? null
        : {
            pinned,
            full: !pinned && pinsFull,
            onToggle: () => {
              setError('');
              togglePin.mutate(
                { commentId: comment.id, pinned },
                {
                  onError: (cause) =>
                    setError(
                      drop3cErrorMessage(
                        cause instanceof Error ? cause.message : '',
                        'That pin could not be changed. Try again.',
                      ),
                    ),
                },
              );
            },
          },
      // Own messages only, in every room kind, never anyone else's even for
      // an admin (design-note "Three-dot menu" 3).
      onEdit: own && !isForward ? () => startEdit(comment, where) : null,
      onDelete: own
        ? () => {
            setDeleteError('');
            setDeleteFor({ comment, isReply });
          }
        : null,
    };
  }

  const editOpenIn = (commentId: string, where: 'room' | 'thread') =>
    editing !== null &&
    editing.roomId === activeRoomId &&
    editing.commentId === commentId &&
    editing.where === where;

  function startEdit(comment: CommentWithAuthor, where: 'room' | 'thread') {
    // The editor takes its value once, when it mounts (CommentEditor), so the
    // draft is set before it renders.
    setEditDraft(comment.body);
    setEditError('');
    setEditing({ roomId: activeRoomId, commentId: comment.id, where });
  }

  function cancelEdit() {
    setEditing(null);
    setEditError('');
  }

  function commitEdit(comment: CommentWithAuthor) {
    if (editMsg.isPending || editUploading) {
      return;
    }
    // Unchanged: close, send nothing.
    if (editDraft === comment.body) {
      cancelEdit();
      return;
    }
    // Messages allows an image-only post, so an image alone is not empty.
    if (isRichTextEmpty(editDraft) && !editDraft.includes('<img')) {
      setEditError('A message cannot be empty. Delete it instead.');
      return;
    }
    if (editDraft.length > COMMENT_BODY_MAX_LENGTH) {
      setEditError('That message is too long. Split it into two.');
      return;
    }
    // The same members-only rule as the composer and a thread reply: refuse
    // before sending, keep the draft, name who cannot be mentioned here.
    // Only a mention ADDED in this edit is checked: one the message already
    // had stays as it is (setCommentMentions inserts only the added ones), so
    // someone who has since left the chat or been deactivated does not block
    // a typo fix.
    const mentioned = personMentionsIn(editDraft);
    const allowed = new Set([
      ...mentionPeople.map((person) => person.id),
      ...comment.mentioned_user_ids,
    ]);
    const refused = mentioned.filter((person) => !allowed.has(person.id));
    if (refused.length > 0) {
      setEditError(mentionRefusedMessage(roomMentionScope(activeRoom), refused));
      return;
    }
    setEditError('');
    editMsg.mutate(
      {
        commentId: comment.id,
        body: editDraft,
        mentionedUserIds: mentioned.map((person) => person.id),
      },
      {
        // Closed only once the save is known to have landed; a failed save
        // keeps the editor open with the draft.
        onSuccess: () => setEditing(null),
        onError: (cause) => {
          const raw = cause instanceof Error ? cause.message : '';
          setEditError(drop3cErrorMessage(raw, commentErrorMessage(raw)));
        },
      },
    );
  }

  function editorFor(comment: CommentWithAuthor) {
    const authorName = comment.author_name === '' ? 'Someone' : comment.author_name;
    return (
      <div
        className="message-row-edit comment-edit"
        onKeyDown={(event: ReactKeyboardEvent<HTMLDivElement>) => {
          // The @ popover closes itself on the first Escape and ProseMirror
          // marks a handled key with preventDefault, so only an unhandled
          // Escape cancels the edit.
          if (event.key === 'Escape' && !event.defaultPrevented) {
            event.preventDefault();
            cancelEdit();
          }
        }}
      >
        <CommentEditor
          label={`Edit message by ${authorName}`}
          value={editDraft}
          onChange={setEditDraft}
          onSubmit={() => commitEdit(comment)}
          client={client}
          sources={{
            cgMember: cgAccess.data === true,
            dealAccess,
            salesMember: salesAccess.data === true,
          }}
          people={mentionPeople}
          commandPeople={everyone}
          commandNavigate={commandNavigate}
          onPasteImage={pasteImageWithCap}
          onUploadingChange={setEditUploading}
          onPasteError={(cause) => {
            const message = cause instanceof Error ? cause.message : '';
            setEditError(message === '' ? pasteImageErrorMessage(cause) : message);
          }}
          enableRecordPicker
        />
        {editError === '' ? null : <InlineError>{editError}</InlineError>}
        <div className="comment-actions">
          <Button variant="ghost" size="sm" onClick={cancelEdit}>
            Cancel
          </Button>
          <Button
            size="sm"
            onClick={() => commitEdit(comment)}
            disabled={editMsg.isPending || editUploading}
          >
            {editMsg.isPending ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </div>
    );
  }

  function rowFor(comment: CommentWithAuthor, isReply: boolean, where: 'room' | 'thread') {
    const rowExtras = extras.data?.get(comment.id);
    const callCard = huddleByCommentId.get(comment.id);
    const isEditing = editOpenIn(comment.id, where);
    // While a row is being edited it has no hover bar and no phone sheet: a
    // tap inside the editor (a contenteditable div, not a textarea) would
    // otherwise open the sheet on a phone.
    const actions = isEditing ? null : actionsFor(comment, isReply, where);
    return (
      <MessageRow
        key={comment.id}
        comment={comment}
        quote={
          comment.quoted_comment_id &&
          visibleSources.data?.get(comment.quoted_comment_id)?.projectId === activeRoomId
            ? (visibleSources.data.get(comment.quoted_comment_id) ?? null)
            : null
        }
        quoteReadStatus={
          visibleSources.isError ? 'error' : visibleSources.isPending ? 'loading' : 'ready'
        }
        onRetryQuote={() => {
          void visibleSources.refetch();
        }}
        onQuoteJump={() => {
          if (comment.quoted_comment_id) void jumpQuote(comment.quoted_comment_id);
        }}
        extras={rowExtras}
        sourceVisible={
          rowExtras?.forwardedFromCommentId != null &&
          visibleSources.data?.has(rowExtras.forwardedFromCommentId) === true
        }
        onViewOriginal={(roomId, commentId) => {
          setSearchParams({ room: roomId });
          setHighlightId(commentId);
        }}
        thumbs={thumbsFor(comment)}
        reactions={reactionsFor(comment)}
        actions={actions}
        thread={isReply ? undefined : split.summaries.get(comment.id)}
        onOpenThread={
          isReply || threadParent?.id === comment.id ? null : () => openThread(comment.id)
        }
        pinned={!isReply && pinnedIds.has(comment.id)}
        highlighted={highlightId === comment.id}
        onOpenSheet={
          touch && actions !== null ? () => setSheetFor({ comment, isReply, where }) : null
        }
        editor={isEditing ? editorFor(comment) : null}
        huddleCard={
          callCard === undefined ? null : (
            <HuddleMessage
              client={client}
              huddle={callCard}
              {...(!readOnly && activeRoom !== null && huddleCall.available
                ? { onJoin: () => huddleCall.join(activeRoom.id, activeTitle) }
                : {})}
              canRetry={
                access.status === 'approved' && access.profile.internal_role === 'tap_admin'
              }
            />
          )
        }
        onOpenLink={openLinkInPlace}
      />
    );
  }

  function submitReply() {
    if (threadParent === null) {
      return;
    }
    if (isRichTextEmpty(threadBody) && !threadBody.includes('<img')) {
      setThreadError('Write a reply first.');
      return;
    }
    // The same mention rule as the room composer: a reply is a room message.
    const mentioned = personMentionsIn(threadBody);
    const allowed = new Set(mentionPeople.map((person) => person.id));
    const refused = mentioned.filter((person) => !allowed.has(person.id));
    if (refused.length > 0) {
      setThreadError(mentionRefusedMessage(roomMentionScope(activeRoom), refused));
      return;
    }
    setThreadError('');
    postReply.mutate(
      {
        parentCommentId: threadParent.id,
        body: threadBody,
        mentionedUserIds: mentioned.map((person) => person.id),
      },
      {
        onSuccess: (result) => {
          setThreadBody('');
          if (result.mentionError !== null) {
            setThreadError(mentionNotDeliveredMessage(result.mentionError));
          }
        },
        onError: (cause) => {
          const raw = cause instanceof Error ? cause.message : '';
          setThreadError(drop3cErrorMessage(raw, commentErrorMessage(raw)));
        },
      },
    );
  }

  function confirmDelete() {
    if (deleteFor === null) {
      return;
    }
    const target = deleteFor.comment;
    setDeleteError('');
    deleteMsg.mutate(target.id, {
      onSuccess: (outcome) => {
        setDeleteFor(null);
        if (outcome === 'deleted' && threadParent?.id === target.id) {
          closeThread();
        }
      },
      onError: (cause) =>
        setDeleteError(
          drop3cErrorMessage(
            cause instanceof Error ? cause.message : '',
            'That message could not be deleted. Try again.',
          ),
        ),
    });
  }

  if (rooms.isPending) {
    return (
      <>
        <PageHeader title="Messages" topbar />
        <Skeleton lines={3} label="Loading rooms" />
      </>
    );
  }

  function renderRoom(room: RoomListItem) {
    return (
      <RoomRow
        key={room.id}
        room={room}
        title={titleOf(room)}
        members={roomMembers.data?.get(room.id)}
        myId={myId}
        active={room.id === activeRoomId}
        preview={latest.data?.get(room.id)}
        unread={unread.data?.get(room.id)}
        onOpen={() => setSearchParams({ room: room.id })}
      />
    );
  }

  // The room composer with its error, notice and reconnect lines. It sits
  // under the list in every room, and above it in the Agents room, where the
  // newest message is on top (D5, newestFirst.ts).
  const composerTopClass = newestFirst ? ' messages-composer--top' : '';
  const composerArea = (
    <>
      {error === '' ? null : <InlineError>{error}</InlineError>}
      {quoteJumpStatus ? (
        <div role="status">
          {quoteJumpStatus}
          <button
            type="button"
            onClick={() => {
              quoteJumpOp.current++;
              setQuoteJumpStatus('');
            }}
          >
            Cancel
          </button>
          {quoteJumpStatus !== 'Finding message…' && quoteJumpStatus !== 'Original unavailable' ? (
            <button type="button" onClick={() => void jumpQuote(quoteTarget.current)}>
              {quoteJumpStatus === 'Keep looking' ? 'Keep looking' : 'Retry'}
            </button>
          ) : null}
        </div>
      ) : null}
      {notice === '' ? null : (
        <p className="messages-notice" role="status">
          {notice}
        </p>
      )}

      {needsReconnect ? (
        <div className="comment-reconnect">
          <ReconnectDriveButton />
          <span className="drive-connection-note">
            Then attach the file again. Your message stays put.
          </span>
        </div>
      ) : null}

      {readOnly ? (
        <div className={`messages-composer messages-composer--readonly${composerTopClass}`}>
          <p className="messages-composer-readonly-note">
            You can read this room. Posting is for TAP members.
          </p>
        </div>
      ) : (
        <div className={`messages-composer${composerTopClass}`}>
          <CommentEditor
            ref={editorRef}
            label={`Message ${activeTitle}`}
            value={body}
            onChange={setBody}
            onSubmit={submit}
            client={client}
            sources={{
              cgMember: cgAccess.data === true,
              dealAccess,
              salesMember: salesAccess.data === true,
            }}
            people={mentionPeople}
            commandPeople={everyone}
            commandNavigate={commandNavigate}
            onPasteImage={pasteImageWithCap}
            onUploadingChange={setUploading}
            onPasteError={(cause) => {
              const message = cause instanceof Error ? cause.message : '';
              setNeedsReconnect(message.toLowerCase().includes('reconnect'));
              setError(message === '' ? pasteImageErrorMessage(cause) : message);
            }}
            enableRecordPicker
          />
          <div className="messages-composer-toolbar">
            <button
              type="button"
              aria-label="Attach a screenshot or file"
              className="messages-attach-button"
              onClick={() => fileInputRef.current?.click()}
            >
              📎
            </button>
            <input
              ref={fileInputRef}
              type="file"
              className="messages-attach-input"
              onChange={(event) => {
                const file = event.target.files?.[0];
                if (file !== undefined) {
                  void handleAttach(file);
                }
                event.target.value = '';
              }}
            />
            <span className="messages-composer-hint">
              Paste a screenshot · @ a person · # a deal, lead or order
            </span>
            <Button onClick={submit} disabled={createComment.isPending || uploading}>
              {createComment.isPending ? 'Sending…' : 'Send'}
            </Button>
          </div>
        </div>
      )}
    </>
  );

  return (
    <>
      <PageHeader title="Messages" topbar />
      <div className="messages-page" data-mobile-pane={mobilePane}>
        <section
          ref={roomsSectionRef}
          aria-label="Rooms"
          className="messages-room-list"
          tabIndex={-1}
        >
          <div className="messages-alerts">
            <DesktopAlertsButton compact />
          </div>
          <div className="messages-room-list-heading">Rooms</div>
          {channels.map(renderRoom)}
          {hasHuddleChat ? (
            <HuddleRow
              active={huddleOpen}
              unread={huddle.data?.unread}
              onOpen={() => setSearchParams({ room: HUDDLE_CHAT_ID })}
            />
          ) : null}

          <div className="messages-room-list-heading messages-room-list-heading--with-action">
            <span>Group chats</span>
            {readOnly ? null : (
              <button
                type="button"
                className="messages-room-add"
                aria-label="New group chat"
                onClick={() => setNewChat('group')}
              >
                +
              </button>
            )}
          </div>
          {groups.length === 0 ? (
            <p className="messages-room-none">No group chats yet</p>
          ) : (
            groups.map(renderRoom)
          )}

          <div className="messages-room-list-heading messages-room-list-heading--with-action">
            <span>Direct messages</span>
            {readOnly ? null : (
              <button
                type="button"
                className="messages-room-add"
                aria-label="New direct message"
                onClick={() => setNewChat('dm')}
              >
                +
              </button>
            )}
          </div>
          {dms.length === 0 ? (
            <p className="messages-room-none">No direct messages yet</p>
          ) : (
            dms.map(renderRoom)
          )}
        </section>

        <section
          aria-label={huddleOpen ? 'Alex Huddle' : activeRoom === null ? 'Room' : activeTitle}
          className="messages-thread"
        >
          {narrow && mobileThreadOpen ? (
            <button
              type="button"
              className="messages-mobile-back"
              ref={mobileBackRef}
              onClick={() => {
                setCreatedRoomId(null);
                setCreatedRoomError(false);
                setSearchParams({});
              }}
            >
              <span aria-hidden="true">←</span> Back to rooms
            </button>
          ) : null}
          {huddleOpen ? (
            <HuddleChatView
              readOnly={readOnly}
              sources={{
                cgMember: cgAccess.data === true,
                dealAccess,
                salesMember: salesAccess.data === true,
              }}
              people={everyone}
            />
          ) : activeRoom === null ? (
            createdRoomId === requestedRoom ? (
              <div className="messages-empty" role="status">
                {createdRoomError ? (
                  <>
                    <InlineError>The new conversation could not be loaded.</InlineError>
                    <button
                      type="button"
                      onClick={() => {
                        if (createdRoomId !== null) void refreshCreatedRoom(createdRoomId);
                      }}
                    >
                      Try again
                    </button>
                  </>
                ) : (
                  'Opening conversation…'
                )}
              </div>
            ) : (
              <div className="messages-empty">Pick a room.</div>
            )
          ) : (
            <>
              <header className="messages-thread-header huddle-header">
                <div className="huddle-header-text">
                  <h2>{activeTitle}</h2>
                  {activeRoom.kind === 'group' ? (
                    <p>{groupMemberLine(roomMembers.data?.get(activeRoom.id), myId)}</p>
                  ) : activeRoom.kind === 'dm' ? null : activeRoom.description ? (
                    <p>{activeRoom.description}</p>
                  ) : null}
                </div>
                <HuddleStartButton
                  roomId={activeRoom.id}
                  roomName={activeTitle}
                  canPost={!readOnly}
                />
              </header>
              <HuddleBar roomId={activeRoom.id} me={myName} />

              <PinsStrip
                items={pinnedItems}
                canUnpin={!readOnly}
                onJump={(commentId) => setHighlightId(commentId)}
                onUnpin={(commentId) =>
                  togglePin.mutate(
                    { commentId, pinned: true },
                    {
                      onError: (cause) =>
                        setError(
                          drop3cErrorMessage(
                            cause instanceof Error ? cause.message : '',
                            'That pin could not be changed. Try again.',
                          ),
                        ),
                    },
                  )
                }
              />
              {newestFirst ? composerArea : null}
              <div className="messages-thread-scroll">
                <div
                  ref={threadBodyRef}
                  className={`messages-thread-body${newestFirst ? '' : ' messages-thread-body--chat'}${dragActive ? ' messages-thread-body--drag' : ''}`}
                  onScroll={onThreadScroll}
                  onDragOver={(event) => {
                    event.preventDefault();
                    if (!readOnly) {
                      setDragActive(true);
                    }
                  }}
                  onDragLeave={() => setDragActive(false)}
                  onDrop={onDrop}
                >
                  {comments.isPending ? (
                    <Skeleton lines={3} label="Loading messages" />
                  ) : comments.isError ? (
                    <p className="task-panel-text">Messages could not be loaded.</p>
                  ) : roomMessages.length === 0 ? (
                    <p className="task-panel-text">No messages yet</p>
                  ) : (
                    <ul
                      className={`message-list${newestFirst ? ' message-list--newest-first' : ''}`}
                    >
                      {[...roomMessages]
                        .sort((a, b) => b.created_at.localeCompare(a.created_at))
                        .map((comment) => rowFor(comment, false, 'room'))}
                    </ul>
                  )}
                </div>
                {awayRoom === activeRoomId ? (
                  <button
                    type="button"
                    className={`messages-jump-latest${newestFirst ? ' messages-jump-latest--top' : ''}`}
                    onClick={jumpToLatest}
                  >
                    Jump to latest
                  </button>
                ) : null}
              </div>

              {newestFirst ? null : composerArea}
            </>
          )}
        </section>

        {huddleOpen || activeRoom === null || threadParent === null ? null : (
          <aside className="messages-thread-panel" aria-label="Thread">
            <header className="messages-thread-panel-header">
              <button
                type="button"
                className="messages-thread-back"
                aria-label={`Back to ${activeTitle}`}
                ref={threadPanelBackRef}
                onClick={closeThread}
              >
                ‹
              </button>
              <div className="messages-thread-panel-title">
                <h3>Thread</h3>
                <p>{activeTitle}</p>
              </div>
              <button
                type="button"
                className="messages-thread-close"
                aria-label="Close thread"
                onClick={closeThread}
              >
                ✕
              </button>
            </header>
            <div className="messages-thread-panel-body">
              <ul className="message-list message-list--thread">
                {rowFor(threadParent, false, 'thread')}
              </ul>
              <div className="messages-thread-count">
                {threadReplies.length} {threadReplies.length === 1 ? 'reply' : 'replies'}
              </div>
              <ul className="message-list message-list--thread">
                {threadReplies.map((reply) => rowFor(reply, true, 'thread'))}
              </ul>
            </div>
            {threadError === '' ? null : <InlineError>{threadError}</InlineError>}
            {readOnly ? (
              <div className="messages-composer messages-composer--readonly">
                <p className="messages-composer-readonly-note">
                  You can read this thread. Posting is for TAP members.
                </p>
              </div>
            ) : threadParentDeleted ? (
              <div className="messages-composer messages-composer--readonly">
                <p className="messages-composer-readonly-note">
                  This message was deleted, so the thread takes no new replies.
                </p>
              </div>
            ) : (
              <div className="messages-composer">
                <CommentEditor
                  label="Reply in thread"
                  value={threadBody}
                  onChange={setThreadBody}
                  onSubmit={submitReply}
                  client={client}
                  sources={{
                    cgMember: cgAccess.data === true,
                    dealAccess,
                    salesMember: salesAccess.data === true,
                  }}
                  people={mentionPeople}
                  commandPeople={everyone}
                  commandNavigate={commandNavigate}
                  enableRecordPicker
                />
                <div className="messages-composer-toolbar">
                  <span className="messages-composer-hint">
                    Replies notify the message author and earlier repliers
                  </span>
                  <Button onClick={submitReply} disabled={postReply.isPending}>
                    {postReply.isPending ? 'Sending…' : 'Reply'}
                  </Button>
                </div>
              </div>
            )}
          </aside>
        )}
      </div>

      {forwardFor === null || activeRoom === null ? null : (
        <ForwardDialog
          comment={forwardFor}
          sourceRoomId={activeRoom.id}
          sourceLabel={activeTitle}
          targets={forwardTargets(rooms.data ?? [], activeRoom.id)}
          titleOf={titleOf}
          onClose={() => setForwardFor(null)}
          onForwarded={(targetTitle) => {
            setForwardFor(null);
            setNotice(`Forwarded to ${targetTitle}.`);
          }}
        />
      )}

      {deleteFor === null ? null : (
        <DeleteMessageDialog
          comment={deleteFor.comment}
          where={activeTitle}
          replyCount={
            deleteFor.isReply ? 0 : (split.replies.get(deleteFor.comment.id)?.length ?? 0)
          }
          pinned={pinnedIds.has(deleteFor.comment.id)}
          busy={deleteMsg.isPending}
          error={deleteError}
          onCancel={() => setDeleteFor(null)}
          onConfirm={confirmDelete}
        />
      )}

      {sheetFor === null
        ? null
        : (() => {
            const sheetActions = actionsFor(sheetFor.comment, sheetFor.isReply, sheetFor.where);
            return sheetActions === null ? null : (
              <MessageActionSheet
                actions={sheetActions}
                reactions={reactionsFor(sheetFor.comment)}
                onClose={() => setSheetFor(null)}
              />
            );
          })()}

      {taskDialogComment === null || activeRoom === null ? null : (
        <MakeTaskDialog
          open
          onClose={() => setTaskDialogComment(null)}
          comment={taskDialogComment}
          sourceRoomName={activeTitle}
          onTaskCreated={(task) => {
            createComment.mutate(
              {
                // The title is free text typed in the dialog: escaped, so a
                // title with < or & reads as typed instead of becoming markup.
                body: `<p>Task: <a href="/projects/${task.projectId}?task=${task.id}">${escapeHtmlText(task.title)}</a> (made from this message)</p>`,
                // Stays empty on purpose: this line is written by the page, not
                // typed by a person, and holds no @ pill (the title is escaped
                // text), so it has nobody to notify.
                mentionedUserIds: [],
              },
              { onSuccess: () => refreshThread(activeRoomId) },
            );
          }}
        />
      )}

      {openRecord === null ? null : (
        <MessageRecordDialog
          key={openRecord.taskId}
          taskId={openRecord.taskId}
          onClose={closeRecord}
          onFallback={followRecordLink}
        />
      )}

      {newChat === null || myId === '' ? null : (
        <NewChatDialog
          mode={newChat}
          myId={myId}
          onClose={() => setNewChat(null)}
          onCreated={(roomId) => {
            setNewChat(null);
            setCreatedRoomId(roomId);
            setCreatedRoomError(false);
            setSearchParams({ room: roomId });
            void refreshCreatedRoom(roomId);
          }}
        />
      )}
    </>
  );
}
